import { FormEvent, useState } from 'react';
import Button from '@/components/Button';
import CustomToast from '@/components/CustomToast';
import { CheckIcon } from '@heroicons/react/24/solid';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import toast from 'react-hot-toast';

const PaymentMethodSetupForm = ({
  clientSecret,
  onSuccess,
}: {
  clientSecret: string;
  onSuccess: () => Promise<void>;
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!stripe || !elements) {
      setMessage('Stripe is not ready yet. Please try again.');
      return;
    }

    setLoading(true);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setMessage(submitError.message || 'Please check your payment details.');
        return;
      }

      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        redirect: 'if_required',
        confirmParams: {
          // Prefer a clean URL (Stripe will append params if it redirects)
          return_url: window.location.href,
        },
      });

      if (error) {
        setMessage(error.message || 'Failed to add payment method.');
        return;
      }

      if (!setupIntent) {
        // If a redirect happened, Stripe will navigate away; if we’re here, it’s unexpected.
        setMessage('Unable to confirm setup. Please try again.');
        return;
      }

      if (setupIntent.status === 'succeeded' || setupIntent.status === 'processing') {
        toast.custom((t) => (
          <CustomToast id={t.id} message="Payment method added successfully!" visible={t.visible} />
        ));

        await onSuccess();
        return;
      }

      setMessage(`Setup status: ${setupIntent.status}`);
    } catch (err) {
      console.error('Failed to confirm payment method setup:', err);
      setMessage('Something went wrong while adding your payment method.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="payment-method-setup-form">
      <div data-testid="payment-method-element">
        <PaymentElement />
      </div>

      {message && (
        <div
          className="rounded-md bg-red-50 p-3 text-sm text-red-700"
          data-testid="payment-method-setup-error"
        >
          {message}
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="submit"
          icon={<CheckIcon className="h-6 w-6" aria-hidden="true" />}
          loadingIconClassName="h-5 w-5 mr-1"
          variant="primary"
          loading={loading}
          disabled={!stripe || !elements}
          data-testid="payment-method-save-button"
        >
          Save payment method
        </Button>
      </div>
    </form>
  );
};

export default PaymentMethodSetupForm;
